import { FC } from "react"
import ConnectorFormButton from "../../components/ConnectorFormButton"

interface OutgoingTestPanelProps {
  dataType: string
  selectedRecordId: string
  onSelectRecord: (id: string) => void
  recentLeads: any[]
  showPreview: boolean
  mappedPreview: any
  isTesting: boolean
  onTest: () => void
  onClear: () => void
  testResult: any
}

const OutgoingTestPanel: FC<OutgoingTestPanelProps> = ({
  dataType,
  selectedRecordId,
  onSelectRecord,
  recentLeads,
  showPreview,
  mappedPreview,
  isTesting,
  onTest,
  onClear,
  testResult
}) => {
  const formatLeadOption = (lead: any): string => {
    const name =
      lead.details?.first_name && lead.details?.last_name
        ? `${lead.details.first_name} ${lead.details.last_name}`
        : lead.details?.first_name || lead.details?.last_name || "Unknown"
    const email = lead.email || "No email"

    // Calculate time ago from timestamp
    let timeAgo = "Unknown time"
    const timestamp = lead.timestamp || lead.created
    if (timestamp) {
      const now = Date.now()
      const createdTimestamp = parseInt(timestamp)
      // Detect if timestamp is in seconds (< year 2286) or milliseconds
      const createdMs =
        createdTimestamp < 10000000000
          ? createdTimestamp * 1000
          : createdTimestamp
      const diffSeconds = Math.floor((now - createdMs) / 1000)

      if (diffSeconds < 0) timeAgo = "just now"
      else if (diffSeconds < 60) timeAgo = "just now"
      else if (diffSeconds < 3600)
        timeAgo = `${Math.floor(diffSeconds / 60)} minutes ago`
      else if (diffSeconds < 86400)
        timeAgo = `${Math.floor(diffSeconds / 3600)} hours ago`
      else timeAgo = `${Math.floor(diffSeconds / 86400)} days ago`
    }

    return `${name} (${email}) - ${timeAgo}`
  }

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Test Outgoing Connection
        </label>
        <p className="text-xs text-gray-500 mb-3">
          Select a {dataType} to preview the mapped data, then send to your CRM
        </p>

        {/* Record Selection */}
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Select a {dataType} to test with:
          </label>
          <select
            value={selectedRecordId}
            onChange={e => onSelectRecord(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select a {dataType}...</option>
            {dataType === "lead" &&
              recentLeads?.slice(0, 10).map((lead: any) => (
                <option key={lead.id} value={lead.id}>
                  {formatLeadOption(lead)}
                </option>
              ))}
          </select>
        </div>

        {/* Preview mapped data */}
        {showPreview && mappedPreview && (
          <div className="bg-blue-50 border border-blue-300 rounded-md p-3 mb-3">
            <div className="text-sm font-medium text-gray-700 mb-2">
              Preview of Mapped Data
            </div>
            <pre className="text-xs bg-white p-2 rounded overflow-auto max-h-64">
              {JSON.stringify(mappedPreview, null, 2)}
            </pre>
          </div>
        )}

        <div className="flex space-x-2">
          <ConnectorFormButton
            onClick={onTest}
            disabled={isTesting || !selectedRecordId}
          >
            {isTesting ? "Sending..." : "Send to CRM"}
          </ConnectorFormButton>
          {selectedRecordId && (
            <ConnectorFormButton onClick={onClear} variant="secondary">
              Clear
            </ConnectorFormButton>
          )}
        </div>
      </div>

      {testResult && (
        <div
          className={`border rounded-md p-3 ${
            testResult.status === "success"
              ? "bg-green-50 border-green-300"
              : "bg-red-50 border-red-300"
          }`}
        >
          <div className="font-medium mb-2">
            {testResult.status === "success"
              ? "✓ Test Successful"
              : "✗ Test Failed"}
          </div>
          {testResult.message && (
            <div className="text-sm mb-2">{testResult.message}</div>
          )}
          {testResult.test_result && (
            <div className="space-y-2">
              <div className="text-xs">
                <span className="font-medium">CRM Response Status: </span>
                <span
                  className={
                    testResult.test_result.status === "success"
                      ? "text-green-600"
                      : "text-red-600"
                  }
                >
                  {testResult.test_result.status}
                </span>
              </div>
              {testResult.test_result.response && (
                <details className="text-xs">
                  <summary className="cursor-pointer font-medium">
                    View CRM Response
                  </summary>
                  <pre className="mt-2 bg-white p-2 rounded overflow-auto max-h-64">
                    {JSON.stringify(testResult.test_result.response, null, 2)}
                  </pre>
                </details>
              )}
              {testResult.test_result.error && (
                <div className="text-sm text-red-600">
                  Error: {testResult.test_result.error}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default OutgoingTestPanel
